import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Box,
  Divider,
  Typography,
        Paper
} from '@mui/material';
import { ChromePicker } from 'react-color';
import { ThemeProvider } from '@mui/material/styles';
// Import all the icons we might need
import {
  WhatsApp as WhatsAppIcon,
  ContentCopy as CopyIcon,
  Email as EmailIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  Link as LinkIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Language as WebIcon,
  ShoppingCart as ShoppingCartIcon,
  Store as StoreIcon,
  LocalShipping as LocalShippingIcon,
  Payment as PaymentIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import menuTheme from '../menuTheme';

const iconComponents = {
  WhatsAppIcon,
  EmailIcon,
  PictureAsPdfIcon,
  PhoneIcon,
  LocationOnIcon,
  LinkIcon,
  InstagramIcon,
  FacebookIcon,
  TwitterIcon,
  LinkedInIcon,
  WebIcon,
  ShoppingCartIcon,
  StoreIcon,
  LocalShippingIcon,
  PaymentIcon,
  InfoIcon
};

const ColorPickerStyles = () => (
  <style>
    {`
      .chrome-picker {
        width: 100% !important;
        margin-top: 0px !important;
        box-shadow: none !important;
        border: 1px solid #e0e0e0 !important;
      }
    `}
  </style>
);

const EditButtonDialog = ({ 
  open, 
  onClose, 
  button, 
  onSave, 
  availableIcons,
  distributor
}) => {
  const [editedButton, setEditedButton] = useState({
    text: '',
    icon: 'WhatsAppIcon', // Default icon since it's required
    href: '',
    hasCopy: false,
    backgroundColor: 'rgb(35, 170, 84)',
    textColor: '#ffffff',
    order: 0,
    ...button
  });
  const [showBgColorPicker, setShowBgColorPicker] = useState(false);
  const [showTextColorPicker, setShowTextColorPicker] = useState(false);

  useEffect(() => {
    setEditedButton({
      text: '',
      icon: 'WhatsAppIcon',
      href: '',
      hasCopy: false,
      backgroundColor: 'rgb(35, 170, 84)',
      textColor: '#ffffff',
      order: 0,
      ...button
    });
  }, [button]);

  // Get the correct icon component
  const IconComponent = iconComponents[editedButton.icon] || WhatsAppIcon;

  return (
    <ThemeProvider theme={menuTheme}>
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
        }
      }}
    >
      <ColorPickerStyles />
      <DialogTitle sx={{ 
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'grey.50',
        px: 3
      }}>
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          Personalizar Botón
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 1.5 }}>
        {/* Preview Section */}
        <Paper 
          elevation={0} 
          sx={{ 
            py: 3, 
            my: 2, 
            px:1,
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2
          }}
        >
          <Typography 
            variant="subtitle2" 
            gutterBottom 
            sx={{ 
              color: 'text.secondary',
              fontWeight: 500,
              mb: 2
            }}
          >
            Vista Previa
          </Typography>
          <Box sx={{ position: 'relative' }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={IconComponent && <IconComponent sx={{fontSize: 24}}/>}
              sx={{ 
                py: 1.5,
                bgcolor: editedButton.backgroundColor,
                color: editedButton.textColor,
                '&:hover': { 
                  bgcolor: `${editedButton.backgroundColor}dd`
                }
              }}
            >
              {editedButton.text || 'Texto del Botón'}
              {editedButton.hasCopy && (
                <Box 
                  sx={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    width: '12%',
                    bgcolor: 'rgba(0, 0, 0, 0.1)',
                    borderLeft: '1px solid rgba(255, 255, 255, 0.29)',
                  }}
                >
                  <CopyIcon sx={{fontSize: 17}}/>
                </Box>
              )}
            </Button>
          </Box>
        </Paper>


        {/* Edit Form */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}> 
                <Typography variant="subtitle2" sx={{fontWeight: 500}}>Cambiar color del botón</Typography>
                  <ChromePicker
                    color={editedButton.backgroundColor}
                    onChange={(color) => setEditedButton({
                      ...editedButton,
                      backgroundColor: color.hex
                    })}
                  />
                </Box>

          <TextField
            label="Texto del Botón"
            value={editedButton.text}
            onChange={(e) => setEditedButton({...editedButton, text: e.target.value})}
            fullWidth
            variant="outlined"
            size="medium"
          />

          <FormControl fullWidth size="medium">
            <InputLabel>Ícono</InputLabel>
            <Select
              value={editedButton.icon}
              onChange={(e) => setEditedButton({...editedButton, icon: e.target.value})}
              label="Ícono"
            >
              <MenuItem value="">Ninguno</MenuItem>
              {availableIcons.map((icon) => {
                const IconComponent = iconComponents[icon];
                return (
                  <MenuItem key={icon} value={icon}>
                    {IconComponent && <IconComponent sx={{ mr: 1 }} />}
                    {icon.replace('Icon', '')}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <TextField
            label="Link (URL)"
            value={editedButton.href || ''}
            onChange={(e) => setEditedButton({...editedButton, href: e.target.value})}
            fullWidth
            variant="outlined"
            size="medium"
          />

          <FormControlLabel
            control={
              <Switch
                checked={editedButton.hasCopy}
                onChange={(e) => setEditedButton({
                  ...editedButton,
                  hasCopy: e.target.checked,
                  copyText: e.target.checked ? editedButton.href : ''
                })}
              />
            }
            label="Incluir botón de copiar"
          />

        
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        p: 3, 
        borderTop: '1px solid',
        borderColor: 'divider'
      }}>
        <Button 
          onClick={() => {
            const defaultButton = {
              ...(editedButton.order === 0 && {
                text: 'Contactar por WhatsApp',
                icon: 'WhatsAppIcon',
                href: `https://wa.me/${distributor.phone?.replace(/\D/g,'')}`,
                hasCopy: true,
                copyText: distributor.phone,
                backgroundColor: 'rgb(35, 170, 84)',
                textColor: '#ffffff',
                order: 0
              }),
              ...(editedButton.order === 1 && {
                text: distributor.email,
                icon: 'EmailIcon',
                href: `mailto:${distributor.email}`,
                hasCopy: true,
                copyText: distributor.email,
                backgroundColor: 'rgb(230, 167, 52)',
                textColor: '#ffffff',
                order: 1
              }),
              ...(editedButton.order === 2 && {
                text: 'Lista en PDF',
                icon: 'PictureAsPdfIcon',
                href: `https://drive.google.com/file/d/${distributor.externalUrls?.[0]}/view`,
                hasCopy: false,
                backgroundColor: 'rgb(8, 161, 149)',
                textColor: '#ffffff',
                order: 2
              })
            };
            setEditedButton(defaultButton);
          }}
          color="warning"
        >
          Restaurar predeterminados
        </Button>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={() => onSave(editedButton)} variant="contained">
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
    </ThemeProvider>
  );
};

export default EditButtonDialog;